import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth-store";
import { Briefcase, Target, CheckSquare, CalendarDays, TrendingUp } from "lucide-react";
import { PipelineFunnel, type FunnelStage } from "@/components/pipeline-funnel";

interface HomeData {
  openDealCount: number;
  untouchedDealCount: number;
  myLeadCount: number;
  myOpenTasks: any[];
  myMeetings: any[];
  todaysLeads: any[];
  myDealsClosingThisMonth: any[];
  myDealsByStage: FunnelStage[];
}

function Stat({ icon: Icon, label, value, to }: { icon: any; label: string; value: number | string; to?: string }) {
  const body = (
    <Card className="transition-colors hover:bg-muted/40">
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function DashboardPage() {
  const me = useAuthStore((s) => s.me);
  const { data, isLoading } = useQuery<HomeData>({
    queryKey: ["/reports/home"],
    queryFn: async () => (await api.get<HomeData>("/reports/home")).data,
  });

  return (
    <>
      <PageHeader
        title={`Welcome, ${me?.name?.split(" ")[0] ?? ""}`}
        description="Your open deals, leads, tasks, and meetings — at a glance."
      />

      <div className="grid gap-4 p-6">
        {/* Stat cards row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Briefcase} label="My Open Deals"      value={data?.openDealCount ?? (isLoading ? "…" : 0)} to="/deals" />
          <Stat icon={TrendingUp} label="My Untouched Deals" value={data?.untouchedDealCount ?? (isLoading ? "…" : 0)} to="/deals" />
          <Stat icon={Target}     label="My Leads"           value={data?.myLeadCount ?? (isLoading ? "…" : 0)} to="/leads" />
          <Stat icon={CheckSquare} label="My Open Tasks"     value={data?.myOpenTasks?.length ?? (isLoading ? "…" : 0)} to="/tasks" />
        </div>

        {/* Pipeline funnel */}
        <PipelineFunnel data={data?.myDealsByStage ?? []} />

        {/* Two-column lists */}
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">My Open Tasks</CardTitle>
              <Link to="/tasks" className="text-xs text-muted-foreground hover:text-foreground">View all</Link>
            </CardHeader>
            <CardContent>
              {data?.myOpenTasks?.length ? (
                <ul className="divide-y text-sm">
                  {data.myOpenTasks.slice(0, 8).map((t) => {
                    const overdue = t.scheduledAt && new Date(t.scheduledAt) < new Date();
                    return (
                      <li key={t.id} className="flex items-center justify-between gap-3 py-2">
                        <Link to="/tasks" className="truncate font-medium hover:underline">{t.subject}</Link>
                        <div className="flex items-center gap-2 text-xs">
                          {t.priority && (
                            <Badge variant={t.priority === "HIGH" ? "destructive" : "secondary"}>{t.priority}</Badge>
                          )}
                          <span className={overdue ? "text-destructive" : "text-muted-foreground"}>
                            {t.scheduledAt ? new Date(t.scheduledAt).toLocaleDateString() : "No due date"}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : <p className="py-6 text-center text-sm text-muted-foreground">No open tasks.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4"/>My Meetings</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.myMeetings?.length ? (
                <ul className="divide-y text-sm">
                  {data.myMeetings.slice(0, 8).map((m) => (
                    <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="truncate font-medium">{m.subject}</span>
                      <span className="text-xs text-muted-foreground">
                        {m.scheduledAt ? new Date(m.scheduledAt).toLocaleString() : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-sm text-muted-foreground">No upcoming meetings.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">Today's Leads</CardTitle>
              <Link to="/leads" className="text-xs text-muted-foreground hover:text-foreground">View all</Link>
            </CardHeader>
            <CardContent>
              {data?.todaysLeads?.length ? (
                <ul className="divide-y text-sm">
                  {data.todaysLeads.map((l) => (
                    <li key={l.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="font-medium">{l.name}</span>
                      <span className="text-xs text-muted-foreground">{l.company ?? l.source ?? ""}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-sm text-muted-foreground">No new leads today.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-base">My Deals Closing This Month</CardTitle>
              <Link to="/deals" className="text-xs text-muted-foreground hover:text-foreground">View all</Link>
            </CardHeader>
            <CardContent>
              {data?.myDealsClosingThisMonth?.length ? (
                <ul className="divide-y text-sm">
                  {data.myDealsClosingThisMonth.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="truncate">
                        <div className="font-medium">{d.name}</div>
                        {d.account?.name && <div className="text-xs text-muted-foreground">{d.account.name}</div>}
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="secondary">{d.stage}</Badge>
                        <span className="tabular-nums">${Number(d.valueUsd).toLocaleString()}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-6 text-center text-sm text-muted-foreground">No deals closing this month.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
